import type { GetStaticPaths, GetStaticProps } from 'next'
import {
  getSiteConfiguration,
  getProductPageByHandle,
} from '@magal/services/sanity-service'
import {
  getShopifyProductExtended,
  getShopifyData,
} from '@magal/services/shopify-service'
import {
  MediaFieldType,
  PageBuilderSection,
  PageResponse,
  Seo,
  ShopifyData,
  ShopifyProductExtended,
} from '@magal/models'
import { DefaultPage } from '@magal/page'
import { captureException, withScope } from '@sentry/nextjs'
import {
  REVALIDATE_NOT_FOUND,
  REVALIDATE_PRODUCT,
  ROUTE_BASE_PRODUCTS,
} from '@magal/configs'
import { fetchServiceWithRetry } from '@magal/utils'

export const getStaticPaths: GetStaticPaths = async () => {
  // const paths: string[] = await getAllShopifyProductPaths()
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<
  PageResponse<PageBuilderSection[]>
> = async ({ params, locale, preview = false }) => {
  const { handle } = params

  if (Array.isArray(handle)) {
    throw new Error('handle must be passed as string. Received array')
  }

  const pageProps: PageResponse<PageBuilderSection[]> | undefined =
    await (async () => {
      // 1. Fetch product from Shopify by handle

      const shopifyRes = await fetchServiceWithRetry<ShopifyProductExtended>(
        () => getShopifyProductExtended(handle, locale),
        5,
      )

      if (shopifyRes.status === 'ERROR') {
        const exception = new Error(
          `[PDP] Shopify function error ${JSON.stringify(shopifyRes.errors)}`,
        )
        captureException(exception)
        throw exception
      }

      const productData = shopifyRes.data
      if (!productData) {
        return undefined
      }
      const shopifyProductData: ShopifyData = {
        productExtended: {
          [productData.id]: productData,
        },
        mainProductId: productData.id,
      }

      // 2. Establish advancedTemplateType
      const advancedTemplateType = productData.advancedTemplate

      // 3. Fetch product page data from Sanity
      const sanityPageRes = await fetchServiceWithRetry<
        PageResponse<PageBuilderSection[]>
      >(
        () =>
          getProductPageByHandle(handle, locale, preview, advancedTemplateType),
        5,
      )

      if (sanityPageRes.status === 'ERROR') {
        const exception = new Error(
          `[PDP] Sanity function error ${JSON.stringify(sanityPageRes.errors)}`,
        )
        captureException(exception)
        throw exception
      }
      //
      // const PRODUCT_CONVERSION_AREA_BASE_SCHEMA: SectionProductConversionAreaProps =
      //   {
      //     _type: 'sectionProductConversionArea',
      //     _key: `sectionProductConversionArea`,
      //     productId: productData.id,
      //     shopifyIds: [],
      //   }
      //
      // const OKENDO_REVIEWS_BASE_SCHEMA: SectionOkendoReviewsProps = {
      //   _type: 'sectionOkendoReviews',
      //   _key: `sectionOkendoReviews`,
      //   productId: productData.id,
      // }
      //
      // if (!sanityPageRes) {
      //   return {
      //     pageBuilder: [
      //       PRODUCT_CONVERSION_AREA_BASE_SCHEMA,
      //       OKENDO_REVIEWS_BASE_SCHEMA,
      //     ],
      //     seo,
      //     shopifyData: shopifyProductData,
      //   }
      // }

      // 4. Fetch shopify products for page builder

      const shopifyData = await (async () => {
        if (sanityPageRes.data !== null) {
          const pageShopifyData = await getShopifyData(
            locale,
            sanityPageRes.data.pageBuilder,
          )

          return {
            ...pageShopifyData,
            ...shopifyProductData,
            mainProductId: productData.id,
          }
        }
      })()

      const ogImage: MediaFieldType = {
        alt: productData.featuredImage?.image.alt ?? '',
        mediaPayload: productData.featuredImage,
      }
      const seo: Seo = {
        metaTitle: productData.seo.title ?? productData.fullTitle,
        metaDescription: productData.seo.description,
        ogAltImage: productData.fullTitle,
        ogDescription: productData.seo.description,
        ogTitle: productData.seo.title ?? productData.fullTitle,
        ogImage,
      }

      return {
        ...sanityPageRes.data,
        seo,
        shopifyData,
      }
    })()

  if (!pageProps) {
    withScope(function (scope) {
      scope.setTag('sampleRate', 'low')
      captureException(
        new Error(`Empty pageProps for ${ROUTE_BASE_PRODUCTS}/${handle}`),
      )
    })
    return {
      notFound: true,
      revalidate: REVALIDATE_NOT_FOUND,
    }
  }

  const siteConfigurationRes = await getSiteConfiguration(locale, preview)

  if (siteConfigurationRes.status === 'ERROR') {
    const exception = new Error(
      `[PDP] Sanity getSiteConfiguration function error ${JSON.stringify(
        siteConfigurationRes.errors,
      )}`,
    )
    captureException(exception)
    throw exception
  }

  const { data: siteConfiguration } = siteConfigurationRes

  return {
    props: {
      ...pageProps,
      siteConfiguration,
    },
    revalidate: REVALIDATE_PRODUCT,
  }
}

export default DefaultPage
